/** Typed `field_config` for ELECTION_POSITION fields (optional position restrictions per election). */
export type ElectionPositionFieldConfig = {
    positionsByElectionId: Record<string, number[]>;
};

export function emptyElectionPositionFieldConfig(): ElectionPositionFieldConfig {
    return { positionsByElectionId: {} };
}

export function parseElectionPositionFieldConfig(fieldDef: { fieldConfig?: string }): ElectionPositionFieldConfig {
    if (!fieldDef.fieldConfig?.trim()) {
        return emptyElectionPositionFieldConfig();
    }
    try {
        const parsed = JSON.parse(fieldDef.fieldConfig) as Partial<ElectionPositionFieldConfig>;
        if (parsed?.positionsByElectionId && typeof parsed.positionsByElectionId === "object") {
            const positionsByElectionId: Record<string, number[]> = {};
            for (const [electionKey, positionIds] of Object.entries(parsed.positionsByElectionId)) {
                if (!Array.isArray(positionIds)) {
                    continue;
                }
                const normalizedIds = positionIds
                    .map((positionId) => Number(positionId))
                    .filter((positionId) => positionId && Number.isFinite(positionId));
                positionsByElectionId[String(electionKey)] = normalizedIds;
            }
            return { positionsByElectionId };
        }
    } catch {
        /* ignore */
    }
    return emptyElectionPositionFieldConfig();
}

export function serializeElectionPositionFieldConfig(config: ElectionPositionFieldConfig): string {
    return JSON.stringify({ positionsByElectionId: config.positionsByElectionId ?? {} });
}

/** Returns allowed position ids when restricted; null means show all positions for the election. */
export function getAllowedPositionIdsForElection(
    fieldDef: { fieldConfig?: string },
    electionId: number | undefined | null,
): number[] | null {
    if (electionId == null) {
        return null;
    }
    const { positionsByElectionId } = parseElectionPositionFieldConfig(fieldDef);
    const allowed = positionsByElectionId[String(electionId)];
    if (!allowed?.length) {
        return null;
    }
    return allowed;
}

export function getElectionIdsFromFieldConfig(fieldDef: { fieldConfig?: string }): number[] {
    const { positionsByElectionId } = parseElectionPositionFieldConfig(fieldDef);
    return Object.keys(positionsByElectionId)
        .map((electionKey) => Number(electionKey))
        .filter((electionId) => electionId && Number.isFinite(electionId));
}
